import { Slot, Video } from 'api';
import cx from 'classnames';
import { Text } from 'components/@tedui';
import ContentContainer from 'components/content-container';
import { Link } from 'components/router';
import { useDidMount } from 'lib/hooks/useDidMount';
import { useIsBreakpointWidth } from 'lib/hooks/useIsBreakpointWidth';
import usePersonalizedContent from 'lib/hooks/useSailthru/usePersonalizedContent';
import { isEmpty, sortBy } from 'lodash-es';
import { motion } from 'motion/react';
import { useEffect, useMemo } from 'react';

import { orderSailthruContent, SailthruFormattedItem } from 'lib/sailthru';
import { useFallbackData } from 'lib/sailthru/fallback';
import StandardCardSkeleton from './Skeleton';
import StandardCard from './StandardCard';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.6,
      staggerChildren: 0.08
    }
  }
};

const headerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.25, 0.4, 0.25, 1]
    }
  }
};

const gridVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.4,
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.7,
      ease: [0.25, 0.4, 0.25, 1]
    }
  }
};

type OrderedVideo = {
  order: number;
  video: Video;
};
type Data = SailthruFormattedItem[] | Slot[] | Video[] | OrderedVideo[];

interface StandardRibbonProps {
  data: Data;
  title: string;
  linkText?: string;
  linkUrl?: string;
  sailthruSectionId?: string;
  mixpanelTitle: string;
  isSailthru?: boolean;
  isProgrammer?: boolean;
  hasLink?: boolean;
  hasSailthruCookie?: boolean;
  fallbackSection?: string;
  hideTopic?: boolean;
  className?: string;
  wrapperClassName?: string;
}

const StandardRibbon = ({
  data,
  title,
  linkText,
  linkUrl,
  isSailthru,
  isProgrammer,
  mixpanelTitle,
  hasLink,
  sailthruSectionId,
  hasSailthruCookie,
  fallbackSection,
  hideTopic,
  className,
  wrapperClassName
}: StandardRibbonProps) => {
  const hasMounted = useDidMount();

  const {
    data: fallbackData,
    loading: isFetchingFallbackData,
    fetchFallbackContent
  } = useFallbackData(fallbackSection ?? '');

  const {
    data: personalizedData,
    refetch: fetchPersonalizedData,
    isFetching: isFetchingPersonalizedData
  } = usePersonalizedContent(sailthruSectionId ?? '');

  useEffect(() => {
    if (hasSailthruCookie && hasMounted) {
      fetchPersonalizedData();
    }
  }, [fetchPersonalizedData, hasSailthruCookie, hasMounted]);

  useEffect(() => {
    if (fallbackSection && isEmpty(data)) {
      fetchFallbackContent?.();
    }
  }, [fetchFallbackContent, fallbackSection, data]);

  const talkData: (Video | SailthruFormattedItem)[] = useMemo(() => {
    if (isSailthru) {
      if (!isEmpty(personalizedData)) {
        return orderSailthruContent(personalizedData);
      }

      if (!isEmpty(data)) {
        return orderSailthruContent(data as SailthruFormattedItem[]);
      }

      if (!isEmpty(fallbackData)) {
        return orderSailthruContent(fallbackData as SailthruFormattedItem[]);
      }

      return [];
    }

    if (isProgrammer) {
      return sortBy(data as Slot[], 'order')
        .slice(0, 4)
        .map(({ video }) => video)
        .filter((video): video is Video => video != null);
    }
    return [];
  }, [data, personalizedData, fallbackData, isProgrammer, isSailthru]);

  const isMobileWidth = useIsBreakpointWidth({
    size: 'sm',
    breakPointType: 'tui'
  });

  const isTabletWidth = useIsBreakpointWidth({
    size: 'md',
    breakPointType: 'tui'
  });

  const ribbonClasses = cx({
    'grid-row-4 mt-4 grid gap-12': isMobileWidth,
    'divide-x-8 -ml-5 mt-4 grid grid-cols-4 gap-5 divide-x-thin':
      !isMobileWidth && !isTabletWidth
  });

  const ribbonCardsClasses = cx('flex w-full', {
    'pl-5': !isMobileWidth && !isTabletWidth,
    'pl-5 last:border-l-thin': isTabletWidth && !isMobileWidth
  });

  if ((isFetchingPersonalizedData || isFetchingFallbackData) && isSailthru) {
    return (
      <StandardCardSkeleton
        ribbonClasses={ribbonClasses}
        isMobileWidth={isMobileWidth}
        ribbonCardsClasses={ribbonCardsClasses}
      />
    );
  }

  if (isEmpty(talkData)) {
    return <></>;
  }

  const primaryLink = (
    <motion.div>
      <Link isZenithLink href={linkUrl}>
        <Text
          variant="link2"
          UNSAFE_className="underline hover:no-underline transition-all duration-200"
          weight="font-semibold"
          useNewTextStyles
        >
          {linkText}
        </Text>
      </Link>
    </motion.div>
  );

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible">
      <ContentContainer className={cx('relative pb-12 pt-0', wrapperClassName)}>
        <motion.div
          className={cx(
            'flex items-center justify-between border-t-thin border-black pt-3',
            className
          )}
          variants={headerVariants}
        >
          <Text variant="label2" weight="font-bold" useNewTextStyles>
            {title}
          </Text>
          {!isMobileWidth && hasLink && primaryLink}
        </motion.div>
        {(isMobileWidth || (!isMobileWidth && !isTabletWidth)) && (
          <motion.div className={ribbonClasses} variants={gridVariants}>
            {talkData?.map(
              (data: Video | SailthruFormattedItem, index: number) => {
                return (
                  <motion.div
                    className={ribbonCardsClasses}
                    key={`data-isMobileWidth-${index}`}
                    variants={cardVariants}
                  >
                    <StandardCard
                      data={data}
                      index={index + 1}
                      mixpanelTitle={mixpanelTitle}
                      hideTopic={hideTopic}
                    />
                  </motion.div>
                );
              }
            )}
          </motion.div>
        )}
        {isTabletWidth && !isMobileWidth && (
          <motion.div
            className="mt-4 grid grid-rows-2 gap-12"
            variants={gridVariants}
          >
            <motion.div
              className="-ml-5 grid grid-cols-2 gap-5"
              variants={gridVariants}
            >
              {talkData
                ?.slice(0, 2)
                .map((data: Video | SailthruFormattedItem, index: number) => {
                  return (
                    <motion.div
                      className={ribbonCardsClasses}
                      key={`data-isTabletWidth-${index}`}
                      variants={cardVariants}
                    >
                      <StandardCard
                        data={data}
                        index={index + 1}
                        mixpanelTitle={mixpanelTitle}
                        hideTopic={hideTopic}
                      />
                    </motion.div>
                  );
                })}
            </motion.div>

            <motion.div
              className="-ml-5 grid grid-cols-2 gap-5"
              variants={gridVariants}
            >
              {talkData
                ?.slice(2, 4)
                .map((data: Video | SailthruFormattedItem, index: number) => {
                  return (
                    <motion.div
                      className={`${ribbonCardsClasses}`}
                      key={`data-${index}`}
                      variants={cardVariants}
                    >
                      <StandardCard
                        data={data}
                        index={index + 1}
                        mixpanelTitle={mixpanelTitle}
                        hideTopic={hideTopic}
                      />
                    </motion.div>
                  );
                })}
            </motion.div>
          </motion.div>
        )}

        {isMobileWidth && hasLink && (
          <motion.div
            className="my-6 flex w-full justify-end"
            variants={headerVariants}
          >
            {primaryLink}
          </motion.div>
        )}
      </ContentContainer>
    </motion.div>
  );
};

export default StandardRibbon;
